import { useLocation } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  CreditCard,
  FlaskConical,
  Flag,
  Gauge,
  LayoutDashboard,
  Map,
  MapPin,
  MessageCircle,
  Palette,
  Receipt,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  Users,
  UtensilsCrossed,
  Wallet,
} from "lucide-react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

// ─────────────────────────────────────────────────────────────────────────────
// Admin navigation model — one source of truth for the sidebar (desktop rail
// and the floating phone sidebar) and the page header's title + description.
// Four sections, each with a Doodle badge; every page is one row with a
// Lucide icon, as in the admin mock-ups.
// ─────────────────────────────────────────────────────────────────────────────

export type NavItem = {
  /** Stable id (also used as the React key). */
  key: string;
  href: string;
  label: string;
  /** One sentence on what the page is for: the header's second line. */
  description: string;
  icon: LucideIcon;
  isActive: boolean;
};

export type NavGroup = {
  key: string;
  header: string;
  /** Doodle badge id for the section parent. */
  badge: string;
  items: NavItem[];
};

export function useAdminNav({
  locale,
  isSuperAdmin,
}: {
  locale: string;
  isSuperAdmin?: boolean;
}): {
  groups: NavGroup[];
  /** All destinations flattened in group order. */
  tabs: NavItem[];
  /** The currently-active destination, if any. */
  activeItem: NavItem | undefined;
} {
  const { pathname } = useLocation();
  const t = useTranslations();
  const s = keys.shell;

  const base = `/${locale}/admin`;
  const storeSubscriptionsHref = `${base}/stores/subscriptions`;
  const on = (href: string) => Boolean(pathname?.startsWith(href));

  const item = (
    key: string,
    path: string,
    label: string,
    description: string,
    icon: LucideIcon,
    isActive = on(`${base}/${path}`),
  ): NavItem => ({ key, href: `${base}/${path}`, label: t(label), description: t(description), icon, isActive });

  const groups: NavGroup[] = [
    {
      key: "overview",
      header: t(s.groupOverview),
      badge: "chart",
      items: [
        {
          key: "dashboard",
          href: base,
          label: t(keys.admin.dashboardTitle),
          description: t(s.descDashboard),
          icon: LayoutDashboard,
          isActive: pathname === base || pathname === `${base}/`,
        },
      ],
    },
    {
      key: "platform",
      header: t(keys.admin.navGroupPlatform),
      badge: "team",
      items: [
        item("sellerAccounts", "sellers", keys.admin.sellerAccountsTitle, s.descSellers, Users),
        item(
          "onlineStores",
          "stores",
          keys.admin.onlineStoresTitle,
          s.descStores,
          Store,
          on(`${base}/stores`) && !on(storeSubscriptionsHref),
        ),
        item("restaurants", "restaurants", keys.admin.restaurantsTitle, s.descRestaurants, UtensilsCrossed),
        item("areas", "areas", keys.areas.nav, s.descAreas, MapPin),
        item("areaSuggestions", "area-suggestions", keys.areaSuggestions.nav, s.descAreaSuggestions, Map),
        item("themes", "themes", keys.themes.nav, s.descThemes, Palette),
      ],
    },
    {
      key: "billing",
      header: t(keys.admin.navGroupBilling),
      badge: "register",
      items: [
        item("storeSubscriptions", "stores/subscriptions", keys.admin.storeSubscriptionsTitle, s.descStoreSubscriptions, CreditCard),
        item(
          "walletTopUpRequests",
          "wallet-top-up-requests",
          keys.admin.subscriptionTopUpRequestsTitle,
          s.descWalletTopUpRequests,
          Wallet,
        ),
        item("subscriptions", "subscriptions", keys.admin.subscriptionsTitle, s.descSubscriptions, Receipt),
      ],
    },
    {
      key: "system",
      header: t(keys.admin.navGroupSystem),
      badge: "gear",
      items: [
        item("integrationLogs", "integration-logs", keys.integrationLogs.nav, s.descIntegrationLogs, ScrollText),
        ...(isSuperAdmin
          ? [
              item("whatsapp", "whatsapp", keys.whatsapp.nav, s.descWhatsapp, MessageCircle),
              item(
                "integrationSimulator",
                "integration-simulator",
                keys.integrationSimulator.nav,
                s.descIntegrationSimulator,
                FlaskConical,
              ),
              item("admins", "admins", keys.admin.adminsTitle, s.descAdmins, ShieldCheck),
              item("appConfigs", "app-configs", keys.appConfigs.title, s.descAppConfigs, SlidersHorizontal),
              item("paidServices", "paid-services", keys.paidServices.nav, s.descPaidServices, Gauge),
              item("featureFlags", "feature-flags", keys.featureFlags.title, s.descFeatureFlags, Flag),
            ]
          : []),
      ],
    },
  ];

  const tabs = groups.flatMap((group) => group.items);
  const activeItem = tabs.find((navItem) => navItem.isActive);

  return { groups, tabs, activeItem };
}
